import { useContext } from 'react';
import _ from 'underscore';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { saveAds } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ActivityContext, ScenarioContext } from '@bingads-webui-react/hoc-with-signal';
import { useWizardService } from '@bingads-webui-react/wizard';
import { ImageRepository } from '@bingads-webui-campaign-react/image-experience';
import { constants, newImageFromMTObject } from '@bingads-webui-campaign-react/image-experience-model';
import { useCopilotLoggers } from '@bingads-webui-campaign-react/copilot-loggers';
import { getAutoCroppedImages } from '@bingads-webui-campaign/image-experience-utils';
import { parseSync } from '@bingads-webui/safe-json';
import { getODataConfig } from '../model/constants';
import { imagePlacementsToImage } from '../model/utl';
import { getVerifiedTrackingSettings } from '../model/verified-tracking-setting';

export const useDisplayAdsRepository = () => {
  const i18n = useContext(I18nContext);

  const serverContext = useContext(ServerContext);
  const scenarioContext = useContext(ScenarioContext);

  const activity = useContext(ActivityContext);
  const { scope } = useContext(ScopeContext);
  const { state } = useWizardService();
  const { logSuggestionsApplied } = useCopilotLoggers();

  const advertiserCustomerId = _.get(serverContext, ['CurrentAccount', 'AdvertiserCustomerID']);

  const imageRepository = new ImageRepository(advertiserCustomerId, scope.values.aid, getODataConfig(serverContext));

  const load = () => {
    const initialAds = state.context.wizardData.savedAdModel;

    if (!initialAds) {
      return Promise.resolve({
        data: [
          {
            uid: Math.random(),
            data: {
              FinalUrls: [],
              image: null,
            },
          },
        ],
      });
    }
    const imageAsset = initialAds.Images[0].Asset;

    const verifiedTrackingSetting = getVerifiedTrackingSettings(initialAds);

    const imagePromise = imageRepository.getImageByIdAsync(imageAsset.IdString)
      .then((imgObjMT) => {
        const imageObject = newImageFromMTObject(imgObjMT, constants.imageSources.Existing);
        const croppedImagePlacements = getAutoCroppedImages({
          originalImageToCrop: imageObject,
          isMultimediaAd: false,
          isMultimediaAdinMSANEnabled: false,
          imageOption: constants.defaultImageOption,
          aspectRatioOption: constants.defaultAspectRatioOption,
          isDisplayAds: true,
        });
        const imageData = imagePlacementsToImage({
          crops: croppedImagePlacements,
          selected: {
            TargetWidth: imageAsset.TargetWidth,
            TargetHeight: imageAsset.TargetHeight,
            CropWidth: imageAsset.CropWidth,
            CropHeight: imageAsset.CropHeight,
            CropX: imageAsset.CropX,
            CropY: imageAsset.CropY,
          },
        });

        const metaData = imageObject.metadata;
        let uriDecodedMetaData = metaData;

        // while saving we only replace the ',' symbol with %2C. So we need to replace it back to ','
        if (metaData) {
          uriDecodedMetaData = metaData.replace(/%2C/g, ',');
        }

        const decodedMetadata = parseSync(uriDecodedMetaData);

        const creativeData = decodedMetadata ? {
          creativeId: imageAsset.IdString,
          creativeMeta: decodedMetadata,
        } : null;

        const { TrackingUrlTemplate, FinalUrlSuffix, UrlCustomParameters, ImpressionTrackingUrls } = initialAds;

        const savedAd = {
          data: [{
            uid: Math.random(),
            Id: initialAds.Id ? initialAds.Id : null,
            data: {
              FinalUrls: initialAds.Id ? initialAds && initialAds.FinalUrls : [],
              creativeData,
              ...imageData.data,
              ...verifiedTrackingSetting,
              TrackingUrlTemplate,
              FinalUrlSuffix,
              UrlCustomParameters,
              ImpressionTrackingUrls,
            },
          }],
        };

        return savedAd;
      });

    return imagePromise;
  };

  const save = ({ data }) => {
    const {
      isCreateAnother,
      onSave,
      onSaveAndCreateAnother,
      isMultiAdFormatAd,
    } = state.context.wizardData;

    return new Promise((resolve, reject) => {
      saveAds({
        ads: isMultiAdFormatAd ? { ...data, adSubType: 'Display' } : data,
        campaign: { Id: scope.values.campaignId },
        adGroup: { Id: scope.values.adGroupId },
        activity,
      }, { scenario: scenarioContext.name, i18n, activityLogger: activity }).then((result) => {
        if (isCreateAnother) {
          onSaveAndCreateAnother(result);
        } else {
          onSave(result);
        }

        const suggestionIds = _.chain(data.data)
          .map(ad => _.get(ad, 'data.creativeData.creativeMeta.recommendationId'))
          .compact()
          .value();

        logSuggestionsApplied(
          scope.values.campaignId,
          scope.values.adGroupId,
          undefined /* textSuggestions */,
          suggestionIds
        );

        resolve();
      }).catch(() => {
        reject();
      });
    });
  };

  return {
    load,
    save,
  };
};
